import { useState, useRef, useEffect } from "react";
import useDebounceValue from "@hooks/useDebounceValue";

interface UseSelectWithSearchProps {
	onSearchChange?: (searchTerm: string) => void;
	searchDebounceMs?: number;
	isSearchEnabled?: boolean;
}

export const useSelectWithSearch = ({
	onSearchChange,
	searchDebounceMs = 500,
	isSearchEnabled = false,
}: UseSelectWithSearchProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);
	const debouncedSearchTerm = useDebounceValue(searchTerm, searchDebounceMs);
	const hasInitialized = useRef(false);

	// Trigger initial load on mount if search is enabled
	useEffect(() => {
		if (isSearchEnabled && !hasInitialized.current && onSearchChange) {
			hasInitialized.current = true;
			onSearchChange("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSearchEnabled]);

	// Trigger search when debounced term changes (but not on initial mount)
	useEffect(() => {
		if (isSearchEnabled && hasInitialized.current && onSearchChange) {
			onSearchChange(debouncedSearchTerm || "");
		}
	}, [debouncedSearchTerm, onSearchChange, isSearchEnabled]);

	const handleClose = () => {
		setIsOpen(false);
		// Clear search after dropdown closes to reset for next time
		// Use setTimeout to avoid clearing before selection is processed
		setTimeout(() => {
			setSearchTerm("");
		}, 300);
	};

	const handleOpen = () => {
		setIsOpen(true);
		// Trigger initial load when dropdown opens if not already initialized
		if (isSearchEnabled && !hasInitialized.current && onSearchChange) {
			hasInitialized.current = true;
			onSearchChange("");
		}
	};

	return {
		searchTerm,
		setSearchTerm,
		isOpen,
		setIsOpen,
		menuRef,
		handleClose,
		handleOpen,
	};
};

